import { useCallback, useRef, useState } from 'react';

interface BeforeAfterSliderProps {
  ratio: number;
  onChange: (ratio: number) => void;
}

export function BeforeAfterSlider({ ratio, onChange }: BeforeAfterSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const updateRatio = useCallback(
    (clientX: number) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const newRatio = Math.max(0, Math.min(1, x / rect.width));
      onChange(newRatio);
    },
    [onChange]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      setIsDragging(true);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      updateRatio(e.clientX);
    },
    [updateRatio]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      updateRatio(e.clientX);
    },
    [isDragging, updateRatio]
  );

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 cursor-ew-resize touch-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {/* Divider line — smooth when not dragging */}
      <div
        className="absolute top-0 h-full w-px bg-white/90 shadow-[0_0_8px_rgba(0,0,0,0.25)]"
        style={{
          left: `${ratio * 100}%`,
          transition: isDragging ? 'none' : 'left 0.05s ease',
        }}
      >
        {/* Handle */}
        <div
          className={`absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg ring-2 transition-all duration-150 ${
            isDragging
              ? 'scale-110 ring-bss-blue shadow-[0_0_16px_rgba(0,102,204,0.35)]'
              : 'ring-white/80 hover:scale-105 hover:ring-bss-blue/60'
          }`}
        >
          <svg
            className="h-5 w-5 text-slate-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 9l4-4 4 4m0 6l-4 4-4-4"
            />
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
        Before
      </div>
      <div className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-bss-blue/85 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
        After
      </div>
    </div>
  );
}
